import { TextField } from './FarChat/ChatFeedbackOverlays';
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  MenuItem,
  FormLabel,
  Paper,
  Typography,
} from '@mui/material';
import {
  downloadStreamlinedMrr,
  loadMarketResearchMrrDetails,
  MarketResearchMrrDetailsResponse,
  MarketResearchMrrReviewCommand,
  MarketResearchMrrReviewField,
  MarketResearchMrrReviewValue,
  saveMarketResearchMrrDetails,
  saveMarketResearchHandoff,
} from '../api/stageFunctions';
import { useFlow } from './StageWrapper';
import { toUserFacingError } from '../utils/userFacingError';

export interface MarketResearchMrrDetailsPanelProps {
  flowId?: string | null;
  /** Stage 11 already provides the document-outline heading in its accordion. */
  embedded?: boolean;
  /** Chat supplies the authenticated flow permission without a StageWrapper. */
  isViewOnly?: boolean;
}

export interface MarketResearchMrrDetailsHandle {
  /** Finish dirty review writes before another stage can replace Stage 11. */
  flushPendingSaves: () => Promise<void>;
}

const SECTION_A_FIELDS = [
  ['organization', 'Organization'],
  ['date', 'Report date', 'date'],
  ['customer', 'Customer'],
  ['estimated_cost', 'Estimated cost'],
  ['purchase_request', 'Purchase request number'],
  ['required_delivery_date', 'Required delivery date'],
] as const;

const FIELD_LABELS: Record<string, string> = {
  date: 'Report date',
  organization: 'Organization',
  customer: 'Customer',
  requirement_title: 'Requirement title',
  requirement_description: 'Requirement description',
  estimated_cost: 'Estimated cost',
  purchase_request: 'Purchase request number',
  psc: 'Primary PSC',
  naics: 'Primary NAICS',
  required_delivery_date: 'Required delivery date',
  conclusions: 'Conclusions and recommendations',
  extent_of_competition: 'Extent of competition',
  procedure: 'Procedure',
  procedure_rationale: 'Procedure rationale',
  contract_type: 'Contract type',
  contract_type_rationale: 'Contract type rationale',
  basis_for_award: 'Basis for award',
};

const APPROACH_FIELDS = [
  ['conclusions', 'Conclusions and recommendations', true],
  ['extent_of_competition', 'Extent of competition'],
  ['procedure', 'Procedure'],
  ['procedure_rationale', 'Procedure rationale', true],
  ['contract_type', 'Contract type'],
  ['contract_type_rationale', 'Contract type rationale', true],
  ['basis_for_award', 'Basis for award', true],
] as const;

// These controls exist only in the native MRR form.  Every source-owned
// field stays at its original workflow stage; these values have no upstream
// owner and must be reviewable before the PDF is exported.
const MRR_REPORT_OPTIONS = [
  ['selector.enterprise_solutions', 'Include Enterprise Solutions section', false],
  ['selector.projected_milestones', 'Include projected milestones section', false],
] as const;

const MRR_COORDINATION_ROLES = [
  ['requiring_activity', 'Requiring activity / PM'],
  ['contracting_officer', 'Contracting officer'],
  ['small_business', 'Small business professional'],
  ['legal', 'Legal'],
  ['competition_advocate', 'Competition advocate'],
  ['additional', 'Additional signature'],
  ['approval_authority', 'Approval authority'],
] as const;

// These are the canonical labels that the native MRR template maps to exactly
// one checkbox. Do not replace them with free text or Synopsis-specific terms.
const MRR_CHECKBOX_CHOICES: Record<string, readonly string[]> = {
  extent_of_competition: [
    'Sole Source', 'Full & Open', 'Mandatory Use', 'Brand Name',
    'Limited Sources', 'Fair Opportunity', 'Competitive Non-DoD',
    'Not Applicable', 'Full & Open Exclusion of Sources',
  ],
  procedure: ['FAR 6', 'FAR 8', 'FAR 12', 'FAR 13', 'FAR 14', 'FAR 15', 'FAR 16', 'FAR 36', 'FAR 37'],
  contract_type: [
    'Fixed Price', 'Time & Material', 'Indefinite Delivery Contract (IDC)',
    'Cost-Reimbursement', 'Labor Hour', 'Award Fee', 'Incentive', 'Other',
  ],
};

const emptyResponse: MarketResearchMrrDetailsResponse = {
  details: {},
  inherited_context: {},
};

function stringValue(value: MarketResearchMrrReviewValue | undefined): string {
  return typeof value === 'string' ? value : '';
}

const MRR_INPUT_FIELDS = new Set([
  'organization', 'date', 'customer', 'estimated_cost', 'purchase_request',
  'required_delivery_date', 'conclusions',
  'extent_of_competition', 'procedure', 'procedure_rationale', 'contract_type',
  'contract_type_rationale', 'basis_for_award',
]);

const MRR_NATIVE_REPORT_FIELDS = new Set([...Array.from(MRR_INPUT_FIELDS), 'requirement_description', 'psc', 'naics']);

const OMITTED_MRR_REVIEW_FIELDS = new Set(['requirement_title', 'methods_used']);

function needsReviewControl(field: string, detail: MarketResearchMrrReviewField | undefined): boolean {
  if (!MRR_INPUT_FIELDS.has(field)) return false;
  if (field === 'date') return true;
  // The API is authoritative about whether a value belongs to its source
  // stage. Do not re-open a settled Synopsis/requirement fact as an MRR edit.
  return !detail?.readonly && !(detail?.status === 'suggested' && Boolean(detail.source));
}

function hasValue(value: MarketResearchMrrReviewValue | undefined): boolean {
  return Array.isArray(value) ? value.length > 0 : Boolean(value);
}

function selectedCheckboxChoice(field: string, value: string, choices: readonly string[]): string {
  if (choices.includes(value)) return value;
  const normalized = value.trim().toLocaleLowerCase().replace(/[-&()]/g, ' ').replace(/\s+/g, ' ');
  if (field === 'procedure') {
    const match = normalized.match(/(?:far )?(?:part )?(6|8|12|13|14|15|16|36|37)$/);
    if (match) return `FAR ${match[1]}`;
  }
  if (field === 'extent_of_competition') {
    if (normalized === 'full and open') return 'Full & Open';
    if (normalized === 'full and open exclusion of sources') return 'Full & Open Exclusion of Sources';
  }
  if (field === 'contract_type') {
    if (normalized.includes('firm fixed') || normalized.includes('fixed price')) return 'Fixed Price';
    if (normalized.includes('time') && normalized.includes('material')) return 'Time & Material';
    if (normalized.includes('indefinite delivery') || normalized === 'idc') return 'Indefinite Delivery Contract (IDC)';
    if (normalized.includes('cost reimbursement')) return 'Cost-Reimbursement';
  }
  return choices.includes(value) ? value : '';
}

/**
 * A narrow Stage-11 review surface. The server resolves every source and
 * candidate; this component only keeps local drafts long enough to make each
 * intentional review decision durable before requesting the server-owned PDF.
 */
export const MarketResearchMrrDetailsPanel = forwardRef<MarketResearchMrrDetailsHandle, MarketResearchMrrDetailsPanelProps>(({ flowId, embedded = false, isViewOnly: viewOnlyOverride }, ref) => {
  const { isViewOnly: flowIsViewOnly } = useFlow();
  const isViewOnly = viewOnlyOverride ?? flowIsViewOnly;
  const [review, setReview] = useState<MarketResearchMrrDetailsResponse>(emptyResponse);
  const reviewRef = useRef(review);
  const [drafts, setDrafts] = useState<Record<string, MarketResearchMrrReviewValue>>({});
  const [, setDirty] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(Boolean(flowId));
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dirtyRef = useRef<Set<string>>(new Set());
  const draftsRef = useRef<Record<string, MarketResearchMrrReviewValue>>({});
  const revisionsRef = useRef<Record<string, number>>({});
  const queuesRef = useRef<Record<string, Promise<void>>>({});

  const applyResponse = useCallback((response: MarketResearchMrrDetailsResponse, field?: string, revision?: number) => {
    if (field && revision !== undefined && revisionsRef.current[field] !== revision) return;
    reviewRef.current = response;
    setReview(response);
    if (field) {
      const nextDrafts = { ...draftsRef.current };
      delete nextDrafts[field];
      draftsRef.current = nextDrafts;
      const nextDirty = new Set(dirtyRef.current);
      nextDirty.delete(field);
      dirtyRef.current = nextDirty;
      setDrafts((current) => {
        const next = { ...current };
        delete next[field];
        return next;
      });
      setDirty((current) => {
        const next = new Set(current);
        next.delete(field);
        return next;
      });
    }
  }, []);

  useEffect(() => {
    if (!flowId) {
      setIsLoading(false);
      reviewRef.current = emptyResponse;
      setReview(emptyResponse);
      setDrafts({});
      draftsRef.current = {};
      setDirty(new Set());
      dirtyRef.current = new Set();
      return undefined;
    }
    let active = true;
    setIsLoading(true);
    setError(null);
    void loadMarketResearchMrrDetails(flowId)
      .then((response) => {
        if (!active) return;
        reviewRef.current = response;
        setReview(response);
        setDrafts({});
        draftsRef.current = {};
        setDirty(new Set());
        dirtyRef.current = new Set();
        revisionsRef.current = {};
        queuesRef.current = {};
      })
      .catch((caught) => { if (active) setError(toUserFacingError(caught)); })
      .finally(() => { if (active) setIsLoading(false); });
    return () => { active = false; };
  }, [flowId]);

  const valueFor = useCallback((field: string): MarketResearchMrrReviewValue => (
    Object.prototype.hasOwnProperty.call(drafts, field) ? drafts[field]
      : (field === 'classification_product_key' ? review.classification_product?.selected_key ?? '' : review.details[field]?.value ?? '')
  ), [drafts, review.classification_product, review.details]);

  const markDirty = useCallback((field: string, value: MarketResearchMrrReviewValue) => {
    draftsRef.current = { ...draftsRef.current, [field]: value };
    dirtyRef.current = new Set(dirtyRef.current).add(field);
    // A response to an older in-flight write must not clear this new draft.
    revisionsRef.current[field] = (revisionsRef.current[field] ?? 0) + 1;
    setDrafts((current) => ({ ...current, [field]: value }));
    setDirty((current) => {
      const next = new Set(current);
      next.add(field);
      return next;
    });
  }, []);

  const saveField = useCallback(async (field: string, command: MarketResearchMrrReviewCommand): Promise<void> => {
    if (!flowId || isViewOnly) return;
    const revision = (revisionsRef.current[field] ?? 0) + 1;
    revisionsRef.current[field] = revision;
    // Each response is a full review snapshot, so writes across fields must
    // resolve in order before another response replaces the displayed model.
    const previous = Promise.all(
      Object.values(queuesRef.current).map((queue) => queue.catch(() => undefined)),
    );
    const request = previous.then(async () => {
      try {
        const response = await saveMarketResearchMrrDetails(flowId, command);
        applyResponse(response, field, revision);
      } catch (caught) {
        if (revisionsRef.current[field] === revision) setError(toUserFacingError(caught));
        throw caught;
      }
    });
    queuesRef.current[field] = request;
    return request;
  }, [applyResponse, flowId, isViewOnly]);

  const saveOverride = useCallback((field: string, value: MarketResearchMrrReviewValue) => {
    void saveField(field, { changes: { [field]: { mode: 'override', value } } }).catch(() => undefined);
  }, [saveField]);

  const handleBlur = useCallback((field: string) => {
    if (!dirtyRef.current.has(field)) return;
    saveOverride(field, draftsRef.current[field] ?? valueFor(field));
  }, [saveOverride, valueFor]);

  const flushPendingSaves = useCallback(async (): Promise<void> => {
    if (!flowId || isViewOnly) return;
    // A failed blur leaves the field dirty, so this step can retry it.
    await Promise.all(Object.values(queuesRef.current).map((queue) => queue.catch(() => undefined)));
    const pending = Array.from(dirtyRef.current);
    try {
      await Promise.all(pending.map((field) => saveField(field, {
        changes: { [field]: { mode: 'override', value: draftsRef.current[field] } },
      })));
      if (reviewRef.current.handoff?.can_capture_handoff) {
        const handoff = await saveMarketResearchHandoff(flowId, { capture: true, revision: reviewRef.current.handoff.revision });
        reviewRef.current = { ...reviewRef.current, handoff };
        setReview(reviewRef.current);
      }
      setError(null);
    } catch (caught) {
      setError(toUserFacingError(caught));
      throw caught;
    }
  }, [flowId, isViewOnly, saveField]);

  useImperativeHandle(ref, () => ({ flushPendingSaves }), [flushPendingSaves]);

  const saveAndExport = useCallback(async () => {
    if (!flowId || isViewOnly || isExporting) return;
    setError(null);
    setIsExporting(true);
    try {
      // Wait for an in-flight blur before retrying only the fields that remain dirty.
      await flushPendingSaves();
      await downloadStreamlinedMrr(flowId);
    } catch (caught) {
      setError(toUserFacingError(caught));
    } finally {
      setIsExporting(false);
    }
  }, [flowId, flushPendingSaves, isExporting, isViewOnly]);

  const directExport = useCallback(async () => {
    if (!flowId || isExporting) return;
    setError(null);
    setIsExporting(true);
    try { await downloadStreamlinedMrr(flowId); } catch (caught) { setError(toUserFacingError(caught)); } finally { setIsExporting(false); }
  }, [flowId, isExporting]);

  const disabled = isViewOnly || isLoading || !flowId;
  // Keep older endpoint payloads and shallow test fixtures readable while the
  // server rolls out the explicit source-owned context envelope.
  const inheritedContext = review.inherited_context ?? {};
  const detailFor = (field: string) => inheritedContext[field] ?? review.details[field];
  const classificationProduct = review.classification_product;
  const selectedProductKey = stringValue(valueFor('classification_product_key'));
  const classificationChoicePending = dirtyRef.current.has('classification_product_key');

  const inheritedFields = [
    ...Object.keys(inheritedContext),
    'organization', 'customer', 'requirement_title', 'requirement_description',
    'purchase_request', 'psc', 'naics',
    ...APPROACH_FIELDS.map(([field]) => field),
  ].filter((field, index, fields) => fields.indexOf(field) === index
    && MRR_NATIVE_REPORT_FIELDS.has(field)
    && !(classificationProduct && (field === 'psc' || field === 'naics'))
    && !OMITTED_MRR_REVIEW_FIELDS.has(field)
    && !needsReviewControl(field, detailFor(field))
    && (Boolean(detailFor(field)?.source) || detailFor(field)?.readonly === true)
    && hasValue(detailFor(field)?.value));

  const productOptionLabel = (option: NonNullable<MarketResearchMrrDetailsResponse['classification_product']>['options'][number]) => (
    [option.name, option.manufacturer, option.model, option.price_display].filter(Boolean).join(' · ')
  );

  const renderClassificationField = (field: 'psc' | 'naics') => {
    const label = field === 'psc' ? 'Primary PSC' : 'Primary NAICS';
    const value = Object.prototype.hasOwnProperty.call(drafts, field) ? stringValue(drafts[field]) : classificationProduct?.[field] || stringValue(detailFor(field)?.value);
    const available = field === 'psc' ? classificationProduct?.available_pscs : classificationProduct?.available_naics;
    const inputId = `streamlined-mrr-${field}`;
    return <Grid item xs={12} md={6} key={field}>
      <FormLabel component="label" htmlFor={inputId} sx={{ display: 'block', mb: 0.75, fontWeight: 600 }}>{label}</FormLabel>
      <TextField id={inputId} fullWidth value={value} disabled={disabled} InputProps={{ readOnly: isViewOnly }}
        inputProps={{ 'aria-label': label, maxLength: field === 'psc' ? 4 : 6 }}
        onChange={event => { if (!isViewOnly) markDirty(field, event.target.value); }} onBlur={() => { if (!isViewOnly) handleBlur(field); }}
        helperText={available && available.length > 1 ? `Available ${field === 'psc' ? 'PSCs' : 'NAICS codes'}: ${available.join(', ')}` : !isViewOnly && !value.trim() ? `Enter a ${field === 'psc' ? 'PSC' : 'NAICS'} code.` : undefined} />
    </Grid>;
  };

  const renderTextField = (field: string, label: string, multiline = false, type?: string) => {
    const detail = review.details[field];
    const choices = MRR_CHECKBOX_CHOICES[field];
    const rawValue = stringValue(valueFor(field));
    const controlValue = choices ? selectedCheckboxChoice(field, rawValue, choices) : rawValue;
    const inputId = `streamlined-mrr-${field}`;
    const labelId = `${inputId}-label`;
    return (
      <Grid item xs={12} md={multiline ? 12 : 6} key={field}>
        <Box sx={{ '&:focus-within > [data-mrr-external-label="true"]': { color: 'primary.main' } }}>
          <FormLabel
            component="label"
            id={labelId}
            htmlFor={choices ? undefined : inputId}
            data-mrr-external-label="true"
            disabled={disabled}
            sx={{ display: 'block', mb: 0.25, fontWeight: 600 }}
          >
            {label}
          </FormLabel>
          <TextField
            id={inputId}
            type={type}
            value={controlValue}
            onChange={(event) => markDirty(field, event.target.value)}
            onBlur={() => handleBlur(field)}
            disabled={disabled}
            InputProps={{ readOnly: isViewOnly }}
            inputProps={choices ? undefined : { 'aria-labelledby': labelId }}
            SelectProps={choices ? { labelId } : undefined}
            multiline={multiline}
            minRows={multiline ? 2 : undefined}
            select={Boolean(choices)}
            fullWidth
          >
            {choices?.map((choice) => <MenuItem key={choice} value={choice}>{choice}</MenuItem>)}
          </TextField>
        </Box>
        {detail?.conflict && <Alert severity="warning" sx={{ mt: 0.5 }}>Conflicting source values require your selection.</Alert>}
      </Grid>
    );
  };

  const renderCheckbox = (field: string, label: string, forceReadOnly = false) => {
    const detail = review.details[field];
    if (!detail) return null;
    const checked = stringValue(valueFor(field)).toLocaleLowerCase() === 'yes';
    const readOnly = isViewOnly || forceReadOnly || detail.readonly;
    return (
      <FormControlLabel
        key={field}
        control={(
          <Checkbox
            checked={checked}
            onChange={readOnly ? undefined : (event) => markDirty(field, event.target.checked ? 'Yes' : '')}
            onBlur={readOnly ? undefined : () => handleBlur(field)}
            disabled={disabled || readOnly}
            inputProps={{ 'aria-label': label, readOnly }}
          />
        )}
        label={label}
      />
    );
  };

  const sectionAInputs = SECTION_A_FIELDS.filter(([field]) => needsReviewControl(field, detailFor(field)));
  const conclusionInputs = APPROACH_FIELDS.slice(0, 1).filter(([field]) => needsReviewControl(field, detailFor(field)));
  const approachInputs = APPROACH_FIELDS.slice(1).filter(([field]) => needsReviewControl(field, detailFor(field)));

  const coordinationFieldsAvailable = MRR_COORDINATION_ROLES.some(([role]) => (
    Boolean(review.details[`coordination.${role}.checked`])
  ));

  return (
    <Paper variant={embedded ? undefined : 'outlined'} elevation={0} sx={{ p: embedded ? 0 : 2, width: '100%', bgcolor: embedded ? 'transparent' : undefined }} data-testid="streamlined-mrr-details-panel">
      {!embedded && (
        <>
          <Typography component="h2" variant="h6" gutterBottom>Streamlined MRR Review &amp; Completion</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Review and complete the supporting market-research report. This does not change the Combined Synopsis/Solicitation.
          </Typography>
        </>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {review.handoff?.context_stale && <Alert severity="warning" sx={{ mb: 2 }}>Research or acquisition details have changed. This saved report is retained; review it before use.</Alert>}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }} data-testid="streamlined-mrr-details-loading">
          <CircularProgress aria-label="Loading MRR details" />
        </Box>
      ) : (
        <Grid container spacing={1}>
          {(classificationProduct || sectionAInputs.length > 0) && <Grid item xs={12}><Typography component="h3" variant="subtitle1">Section A — General Contract Information</Typography></Grid>}
          {classificationProduct && (
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'action.hover' }} data-testid="streamlined-mrr-classification-product">
                {classificationProduct.options.length > 0 && <><FormLabel
                  component="label"
                  id="streamlined-mrr-classification-product-label"
                  data-mrr-external-label="true"
                  sx={{ display: 'block', mb: 0.75, fontWeight: 600 }}
                >
                  Product used for PSC and NAICS
                </FormLabel>
                <TextField
                  id="streamlined-mrr-classification-product"
                  select
                  fullWidth
                  value={selectedProductKey}
                  onChange={(event) => {
                    const key = event.target.value;
                    markDirty('classification_product_key', key);
                    saveOverride('classification_product_key', key);
                  }}
                  disabled={disabled}
                  SelectProps={{ labelId: 'streamlined-mrr-classification-product-label' }}
                >
                  {classificationProduct.options.map((option) => (
                    <MenuItem key={option.key} value={option.key}>{productOptionLabel(option)}</MenuItem>
                  ))}
                </TextField></>}
                {classificationProduct.options.length > 0 && classificationProduct.no_usable_prices && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    No usable prices were found among the discovered products. Select the product whose codes belong in this report.
                  </Typography>
                )}
                {classificationChoicePending ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Saving product choice…</Typography>
                ) : (
                  <Grid container spacing={2} sx={{ mt: 0 }}>{(['psc', 'naics'] as const).map(renderClassificationField)}</Grid>
                )}
              </Paper>
            </Grid>
          )}
          {sectionAInputs.map(([field, label, type]) => renderTextField(field, label, false, type))}

          {conclusionInputs.length > 0 && <Grid item xs={12}><Typography component="h3" variant="subtitle1">Section F — Conclusions and recommendations</Typography></Grid>}
          {conclusionInputs.map(([field, label, multiline]) => renderTextField(field, label, multiline))}
          {approachInputs.length > 0 && <Grid item xs={12}><Typography component="h3" variant="subtitle1">Proposed acquisition approach</Typography></Grid>}
          {approachInputs.map(([field, label, multiline]) => renderTextField(field, label, multiline))}

          {inheritedFields.length > 0 && (
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'action.hover' }} data-testid="streamlined-mrr-inherited-context">
                <Typography variant="subtitle2">Auto-filled from previous stages and MRR defaults</Typography>
                <Grid container spacing={1} sx={{ mt: 0.25 }}>
                  {inheritedFields.map((field) => {
                    const detail = detailFor(field);
                    const value = Array.isArray(detail.value) ? detail.value.join(', ') : detail.value;
                    return (
                      <Grid item xs={12} md={field === 'requirement_description' || field.endsWith('rationale') || field === 'conclusions' ? 12 : 6} key={field}>
                        <Typography variant="body2"><strong>{FIELD_LABELS[field] ?? field}:</strong> {value}</Typography>
                        <Typography variant="caption" color="text.secondary">{detail.source?.label}</Typography>
                      </Grid>
                    );
                  })}
                </Grid>
              </Paper>
            </Grid>
          )}

          {MRR_REPORT_OPTIONS.some(([field]) => Boolean(review.details[field])) && (
            <Grid item xs={12}>
              <Typography component="h3" variant="subtitle1">Report options</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', mt: 0.5 }}>
                {MRR_REPORT_OPTIONS.map(([field, label, readOnly]) => renderCheckbox(field, label, readOnly))}

              </Box>
            </Grid>
          )}

          {coordinationFieldsAvailable && (
            <Grid item xs={12}>
              <Typography component="h3" variant="subtitle1" sx={{ mb: 1 }}>Coordination and approval</Typography>
              <Grid container spacing={1}>
                {MRR_COORDINATION_ROLES.map(([role, roleLabel]) => {
                  const base = `coordination.${role}`;
                  if (!review.details[`${base}.checked`]) return null;
                  return (
                    <Grid item xs={12} key={role}>
                      <Paper variant="outlined" sx={{ p: 1 }}>
                        <Typography variant="subtitle2">{roleLabel}</Typography>
                        <Grid container spacing={1} sx={{ mt: 0.25 }}>
                          <Grid item xs={12}>{renderCheckbox(`${base}.checked`, `${roleLabel} participating`)}</Grid>
                          {renderTextField(`${base}.name`, `${roleLabel} name`)}
                          {renderTextField(`${base}.date`, `${roleLabel} date`, false, 'date')}
                          {renderTextField(`${base}.signature`, `${roleLabel} signature`)}
                          {renderTextField(`${base}.comments`, `${roleLabel} comments`, true)}
                        </Grid>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            </Grid>
          )}
          <Grid item xs={12}>
            <Typography variant="body2" color="error" sx={{ fontWeight: 'bold' }} data-testid="streamlined-mrr-export-notice">
              Fields marked [TBD] need information. Applicable form and signature requirements still apply; saved text is not official approval.
            </Typography>
          </Grid>
          <Grid item xs={12}>
            {isViewOnly ? (
              <Button type="button" variant="contained" onClick={directExport} disabled={!flowId || isExporting} startIcon={isExporting ? <CircularProgress size={20} color="inherit" /> : <PictureAsPdfIcon />}>
                {isExporting ? 'Exporting streamlined MRR…' : 'Export streamlined MRR'}
              </Button>
            ) : (
              <Button type="button" variant="contained" onClick={saveAndExport} disabled={!flowId || isExporting || isLoading} startIcon={isExporting ? <CircularProgress size={20} color="inherit" /> : <PictureAsPdfIcon />}>
                {isExporting ? 'Saving & exporting MRR…' : 'Save & export MRR'}
              </Button>
            )}
          </Grid>
        </Grid>
      )}
    </Paper>
  );
});
